import React, { useState, useEffect, useMemo } from 'react';
import { X, Bus as BusIcon, AlertCircle } from 'lucide-react';

const money = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '0.00';
  return Number(v).toFixed(2);
};

/**
 * Admin edit for a single bus enrolment row.
 *
 * BusEnrollments holds ONE ROW PER TRIMESTER, so this edits only the trimester
 * shown in the header. A mid-year line change means editing each affected
 * trimester separately — deliberate, so a settled trimester is never rewritten
 * by accident.
 *
 * Money: TrimesterPrice is stored gross and DiscountAmount separately; the
 * family owes price − discount. The preview below shows exactly what the
 * family will owe after saving.
 */
const BusEditEnrollmentModal = ({
  enrollment, lines, apiCall, onClose, onSuccess, showError,
}) => {
  const [form, setForm] = useState({
    lineId: String(enrollment.LineID || ''),
    overridePrice: '',
    busService: enrollment.BusService || 'Both',
    hasDifferentBuses: !!enrollment.HasDifferentBuses,
    morningBusNumber: enrollment.MorningBusNumber ?? enrollment.BusNumber ?? '',
    eveningBusNumber: enrollment.EveningBusNumber ?? enrollment.BusNumber ?? '',
    useCustomAddress: !!enrollment.UseCustomAddress,
    hasDifferentAddresses: !!enrollment.HasDifferentAddresses,
    morningAddress: enrollment.MorningAddress || '',
    eveningAddress: enrollment.EveningAddress || '',
    addressNotes: enrollment.AddressNotes || '',
    discountAmount: String(enrollment.DiscountAmount || 0),
    discountReason: '',
  });
  const [saving, setSaving] = useState(false);

  const alreadyPaid = Number(enrollment.AmountPaid) || 0;

  const selectedLine = useMemo(
    () => (lines || []).find((l) => String(l.LineID) === String(form.lineId)),
    [lines, form.lineId]
  );

  const preview = useMemo(() => {
    const linePrice = selectedLine ? Number(selectedLine.TrimesterPrice) : Number(enrollment.TrimesterPrice);
    const price = form.overridePrice !== '' ? parseFloat(form.overridePrice) : linePrice;
    const discount = parseFloat(form.discountAmount) || 0;
    if (isNaN(price)) return null;
    const net = Math.round((price - discount) * 100) / 100;
    return {
      price,
      discount,
      net,
      balance: Math.round((net - alreadyPaid) * 100) / 100,
      discountTooBig: discount > price,
      // Reducing the price below what the family already paid is a refund
      // decision, not an edit — the backend refuses it too.
      wouldOverpay: alreadyPaid > net,
    };
  }, [selectedLine, form.overridePrice, form.discountAmount, enrollment.TrimesterPrice, alreadyPaid]);

  useEffect(() => {
    // Moving to a different line adopts that line's price unless the admin
    // has deliberately typed an override.
    if (form.overridePrice !== '') return;
  }, [form.lineId, form.overridePrice]);

  const blocked = !preview || preview.discountTooBig || preview.wouldOverpay || saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (blocked) return;
    try {
      setSaving(true);
      const result = await apiCall(`/bus/enrollments/${enrollment.BusEnrollmentID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lineId: parseInt(form.lineId, 10),
          overridePrice: form.overridePrice === '' ? null : parseFloat(form.overridePrice),
          busService: form.busService,
          hasDifferentBuses: form.hasDifferentBuses,
          morningBusNumber: form.morningBusNumber === '' ? null : parseInt(form.morningBusNumber, 10),
          eveningBusNumber: form.eveningBusNumber === '' ? null : parseInt(form.eveningBusNumber, 10),
          useCustomAddress: form.useCustomAddress,
          hasDifferentAddresses: form.hasDifferentAddresses,
          morningAddress: form.morningAddress || null,
          eveningAddress: form.eveningAddress || null,
          addressNotes: form.addressNotes || null,
          discountAmount: parseFloat(form.discountAmount) || 0,
          discountReason: form.discountReason || null,
        }),
      });
      onSuccess(result);
    } catch (error) {
      showError('Error saving enrollment: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setBool = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

  const showMorning = form.busService === 'Both' || form.busService === 'MorningOnly';
  const showEvening = form.busService === 'Both' || form.busService === 'EveningOnly';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50 flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <BusIcon className="w-6 h-6 text-blue-600" />
              Edit Bus Enrollment
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              <strong>{enrollment.StudentName || `${enrollment.FirstName || ''} ${enrollment.LastName || ''}`.trim()}</strong>
              {' · '}Trimester {enrollment.Trimester}
              {' · '}Family {enrollment.FamilyID}
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Editing trimester {enrollment.Trimester} only — other trimesters are untouched.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Line + price */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Line</label>
              <select
                value={form.lineId}
                onChange={set('lineId')}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                {(lines || []).map((l) => (
                  <option key={l.LineID} value={l.LineID}>
                    {l.LineName} — ${money(l.TrimesterPrice)}/trimester
                  </option>
                ))}
              </select>
              {selectedLine && (
                <p className="text-xs text-gray-500 mt-1">{selectedLine.RouteDescription}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Price override <span className="font-normal text-gray-500">(optional)</span>
              </label>
              <input
                type="number" min="0" step="0.01"
                value={form.overridePrice}
                onChange={set('overridePrice')}
                placeholder={selectedLine ? money(selectedLine.TrimesterPrice) : ''}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Leave blank to use the line's price.</p>
            </div>
          </div>

          {/* Discount */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Discount</label>
              <input
                type="number" min="0" step="0.01"
                value={form.discountAmount}
                onChange={set('discountAmount')}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Reason for change</label>
              <input
                type="text"
                value={form.discountReason}
                onChange={set('discountReason')}
                placeholder="e.g. sibling discount, corrected line"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Buses */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Service</label>
                <select
                  value={form.busService}
                  onChange={set('busService')}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Both">Morning + Evening</option>
                  <option value="MorningOnly">Morning only</option>
                  <option value="EveningOnly">Evening only</option>
                </select>
              </div>
              {form.busService === 'Both' && (
                <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
                  <input type="checkbox" checked={form.hasDifferentBuses}
                    onChange={setBool('hasDifferentBuses')} className="w-4 h-4" />
                  Different bus each way
                </label>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {showMorning && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Morning bus #</label>
                  <input type="number" value={form.morningBusNumber} onChange={set('morningBusNumber')}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
              {showEvening && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Evening bus #</label>
                  <input type="number" value={form.eveningBusNumber} onChange={set('eveningBusNumber')}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
            </div>
          </div>

          {/* Addresses */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.useCustomAddress}
                  onChange={setBool('useCustomAddress')} className="w-4 h-4" />
                Custom pickup / drop-off address
              </label>
              {form.useCustomAddress && form.busService === 'Both' && (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.hasDifferentAddresses}
                    onChange={setBool('hasDifferentAddresses')} className="w-4 h-4" />
                  Different address each way
                </label>
              )}
            </div>
            {form.useCustomAddress && (
              <div className="grid grid-cols-1 gap-3">
                {showMorning && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {form.hasDifferentAddresses ? 'Morning (pickup) address' : 'Address'}
                    </label>
                    <input type="text" value={form.morningAddress} onChange={set('morningAddress')}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                  </div>
                )}
                {showEvening && form.hasDifferentAddresses && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Evening (drop-off) address</label>
                    <input type="text" value={form.eveningAddress} onChange={set('eveningAddress')}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Notes for the driver</label>
                  <input type="text" value={form.addressNotes} onChange={set('addressNotes')}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            )}
          </div>

          {/* What the family will owe */}
          {preview && (
            <div className={`rounded-lg p-4 border ${
              preview.discountTooBig || preview.wouldOverpay
                ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
            }`}>
              {preview.discountTooBig ? (
                <p className="text-sm text-red-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Discount (${money(preview.discount)}) cannot exceed the price (${money(preview.price)}).
                </p>
              ) : preview.wouldOverpay ? (
                <p className="text-sm text-red-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    This family has already paid <strong>${money(alreadyPaid)}</strong>, more than the
                    new price of <strong>${money(preview.net)}</strong>. Void the excess payment first,
                    then make this change.
                  </span>
                </p>
              ) : (
                <div className="text-sm text-blue-900 space-y-1">
                  <div className="flex justify-between">
                    <span>Trimester price</span><span>${money(preview.price)}</span>
                  </div>
                  {preview.discount > 0 && (
                    <div className="flex justify-between text-green-700">
                      <span>Discount</span><span>−${money(preview.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold border-t border-blue-200 pt-1">
                    <span>Family owes</span><span>${money(preview.net)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Already paid</span><span>${money(alreadyPaid)}</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span>Remaining</span>
                    <span className={preview.balance > 0 ? 'text-red-600' : 'text-green-700'}>
                      ${money(preview.balance)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} disabled={saving}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-semibold">
              Cancel
            </button>
            <button type="submit" disabled={blocked}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold">
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusEditEnrollmentModal;
